import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  Kbd,
  useColorModeValue,
  Spinner,
  Flex,
  Divider,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useAuth } from '../../contexts/shared/AuthContext';
import { useLanguage } from '../../contexts/shared/LanguageContext';
import { isConvexOrgId } from '../../utils/accountingUtils';
import {
  FiSearch,
  FiFileText,
  FiCreditCard,
  FiShoppingCart,
  FiPackage,
  FiUsers,
  FiTarget,
  FiTruck,
  FiShoppingBag,
  FiSettings,
  FiArrowRight,
  FiCheckCircle,
  FiLayers,
  FiCalendar,
  FiGlobe,
  FiBriefcase,
  FiDollarSign,
  FiCornerDownLeft,
  FiMail,
} from 'react-icons/fi';
import { SaudiRiyalIcon } from '../icons/SaudiRiyalIcon';

export interface CommandPaletteAction {
  id: string;
  title: string;
  titleAr?: string;
  category: 'quick_action' | 'navigation' | 'record';
  categoryLabel: string;
  description?: string;
  icon: any;
  path: string;
  badge?: string;
  badgeColor?: string;
  keywords: string[];
  requiredRole?: string[];
  requiredModel?: string;
  onSelect?: () => void;
}

interface UniversalCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UniversalCommandPalette: React.FC<UniversalCommandPaletteProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const { user, selectedOrganization, activeVendor } = useAuth();
  const { t, isRtl } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // User role helpers
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin || user?.id === 'local-demo-user' || !user;
  const isVendor = !isAdmin && user?.role === 'vendor';
  const role = isVendor ? 'vendor' : (isAdmin ? 'admin' : (user?.role || 'employee'));

  // Color scheme
  const modalBg = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('gray.50', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const activeItemBg = useColorModeValue('blue.50', 'blue.900');
  const textPrimary = useColorModeValue('gray.800', 'gray.100');
  const textMuted = useColorModeValue('gray.500', 'gray.400');
  const highlightBorder = useColorModeValue('blue.500', 'blue.400');

  // Static Quick Actions & Direct Navigation (Role-Filtered)
  const availableActions: CommandPaletteAction[] = useMemo(() => {
    const list: CommandPaletteAction[] = [];

    // 1. QUICK ACTIONS ("Make...", "Create...", "Add...")
    if (!isVendor) {
      list.push(
        {
          id: 'action-make-quotation',
          title: 'Make Quotation',
          titleAr: 'إنشاء عرض سعر جديد',
          category: 'quick_action',
          categoryLabel: 'Quick Actions (إجراءات سريعة)',
          description: 'Draft formal quotation with items, WBS, pricing & margins',
          icon: FiFileText,
          path: '/quotations/create',
          badge: '⚡ Action',
          badgeColor: 'purple',
          keywords: ['make quotation', 'new quote', 'create quote', 'quote', 'quotation', 'عرض سعر', 'تسعير'],
        },
        {
          id: 'action-make-invoice',
          title: 'Make Tax Invoice (ZATCA)',
          titleAr: 'إصدار فاتورة ضريبية إلكترونية',
          category: 'quick_action',
          categoryLabel: 'Quick Actions (إجراءات سريعة)',
          description: 'Generate Phase-2 compliant ZATCA e-invoice with QR code',
          icon: FiCreditCard,
          path: '/invoices',
          badge: '⚡ Action',
          badgeColor: 'green',
          keywords: ['make invoice', 'new invoice', 'create invoice', 'bill', 'tax invoice', 'فاتورة', 'فاتورة ضريبية'],
        },
        {
          id: 'action-make-po',
          title: 'Make Purchase Order (PO)',
          titleAr: 'إنشاء أمر شراء للمورد',
          category: 'quick_action',
          categoryLabel: 'Quick Actions (إجراءات سريعة)',
          description: 'Issue official Purchase Order to approved supplier',
          icon: FiShoppingCart,
          path: '/procurement',
          badge: '⚡ Action',
          badgeColor: 'orange',
          keywords: ['make po', 'purchase order', 'create po', 'new po', 'order', 'أمر شراء', 'شراء'],
        },
        {
          id: 'action-make-pr',
          title: 'Make Purchase Requisition (PR)',
          titleAr: 'طلب شراء مواد ومستلزمات',
          category: 'quick_action',
          categoryLabel: 'Quick Actions (إجراءات سريعة)',
          description: 'Create internal material purchase requisition for approval',
          icon: FiShoppingCart,
          path: '/procurement',
          badge: '⚡ Action',
          badgeColor: 'orange',
          keywords: ['make pr', 'requisition', 'purchase requisition', 'طلب شراء'],
        },
        {
          id: 'action-make-delivery-note',
          title: 'Make Delivery Note / Dispatch',
          titleAr: 'إنشاء سند تسليم بضاعة',
          category: 'quick_action',
          categoryLabel: 'Quick Actions (إجراءات سريعة)',
          description: 'Issue goods handover note and shipment tracking',
          icon: FiTruck,
          path: '/delivery-notes',
          badge: '⚡ Action',
          badgeColor: 'cyan',
          keywords: ['make delivery note', 'delivery note', 'dispatch', 'handover', 'سند تسليم', 'إرسالية'],
        },
        {
          id: 'action-add-inventory',
          title: 'Add Inventory Item',
          titleAr: 'إضافة صنف أو منتج للمخزون',
          category: 'quick_action',
          categoryLabel: 'Quick Actions (إجراءات سريعة)',
          description: 'Add new SKU, stock quantity, cost and selling price',
          icon: FiPackage,
          path: '/inventory',
          badge: '⚡ Action',
          badgeColor: 'amber',
          keywords: ['add inventory', 'new product', 'add item', 'stock item', 'sku', 'صنف', 'مخزون'],
        },
        {
          id: 'action-add-customer',
          title: 'Add Corporate Client / Account',
          titleAr: 'إضافة عميل أو حساب شركة',
          category: 'quick_action',
          categoryLabel: 'Quick Actions (إجراءات سريعة)',
          description: 'Register corporate customer with CR, VAT and credit limit',
          icon: FiUsers,
          path: '/customers',
          badge: '⚡ Action',
          badgeColor: 'blue',
          keywords: ['add client', 'add customer', 'new client', 'new customer', 'عميل', 'حساب عميل'],
        },
        {
          id: 'action-add-lead',
          title: 'Add Sales Lead / Opportunity',
          titleAr: 'إضافة فرصة بيعية جديدة',
          category: 'quick_action',
          categoryLabel: 'Quick Actions (إجراءات سريعة)',
          description: 'Log new pipeline opportunity and sales stage',
          icon: FiTarget,
          path: '/leads',
          badge: '⚡ Action',
          badgeColor: 'teal',
          keywords: ['add lead', 'new lead', 'sales opportunity', 'فرصة بيعية', 'فرصة'],
        },
        {
          id: 'action-add-supplier',
          title: 'Add Supplier / Vendor',
          titleAr: 'إضافة مورد معتمد جديد',
          category: 'quick_action',
          categoryLabel: 'Quick Actions (إجراءات سريعة)',
          description: 'Add supplier profile, CR number and payment terms',
          icon: FiTruck,
          path: '/suppliers',
          badge: '⚡ Action',
          badgeColor: 'yellow',
          keywords: ['add supplier', 'new supplier', 'add vendor', 'مورد', 'موردين'],
        }
      );
    }

    // HR Actions (Available to all or HR admins)
    list.push(
      {
        id: 'action-apply-leave',
        title: 'Apply for Leave (HR)',
        titleAr: 'تقديم طلب إجازة (سنوية، مرضية، طارئة)',
        category: 'quick_action',
        categoryLabel: 'Quick Actions (إجراءات سريعة)',
        description: 'Calendar date selection: Annual, Sick, Marriage, Bereavement, Hajj',
        icon: FiCalendar,
        path: '/hr',
        badge: '⚡ Self-Service',
        badgeColor: 'purple',
        keywords: ['apply leave', 'request leave', 'annual leave', 'sick leave', 'vacation', 'إجازة', 'طلب إجازة'],
      }
    );

    if (isAdmin || role === 'hr' || role === 'manager') {
      list.push(
        {
          id: 'action-wps-payroll',
          title: 'WPS Payroll & SIF File Export',
          titleAr: 'مسير الرواتب وحماية الأجور (مُدد / SIF)',
          category: 'quick_action',
          categoryLabel: 'Quick Actions (إجراءات سريعة)',
          description: 'Process monthly salaries, GOSI deductions and export SIF',
          icon: SaudiRiyalIcon,
          path: '/hr',
          badge: '⚡ Action',
          badgeColor: 'purple',
          keywords: ['wps', 'payroll', 'sif', 'salaries', 'gosi', 'مسير الرواتب', 'رواتب', 'حماية الأجور'],
        },
        {
          id: 'action-exit-reentry',
          title: 'Exit / Re-Entry Visa & Muqeem Tasks',
          titleAr: 'مهام تأشيرات الخروج والعودة (مقيم)',
          category: 'quick_action',
          categoryLabel: 'Quick Actions (إجراءات سريعة)',
          description: 'Process international travel visas and final exit clearances',
          icon: FiGlobe,
          path: '/hr',
          badge: '⚡ Action',
          badgeColor: 'blue',
          keywords: ['exit reentry', 'muqeem', 'visa', 'final exit', 'خروج وعودة', 'مقيم', 'تأشيرة'],
        }
      );
    }

    // POS Terminal (Available to all)
    list.push({
      id: 'action-pos',
      title: 'Open POS Cashier Terminal',
      titleAr: 'فتح نقطة البيع السريعة والكاشير',
      category: 'quick_action',
      categoryLabel: 'Quick Actions (إجراءات سريعة)',
      description: 'Quick barcode checkout and instant ZATCA receipt',
      icon: FiShoppingBag,
      path: '/pos',
      badge: '⚡ Action',
      badgeColor: 'teal',
      keywords: ['pos', 'cashier', 'checkout', 'terminal', 'نقطة بيع', 'كاشير'],
    });

    // Email & RFQ Hub (Available to all)
    list.push({
      id: 'action-open-email',
      title: 'Open ProcureFlow Mail & Corporate Webmail',
      titleAr: 'فتح البريد الإلكتروني وإدارة المراسلات',
      category: 'quick_action',
      categoryLabel: 'Quick Actions (إجراءات سريعة)',
      description: 'Open ProcureFlow email archive, RFQ radar or live cloud webmail',
      icon: FiMail,
      path: '/procurement/email',
      badge: '⚡ Sourcing',
      badgeColor: 'blue',
      keywords: ['email', 'mail', 'webmail', 'inbox', 'procureflow', 'cpanel', 'outlook', 'بريد', 'إيميل'],
    });

    // 2. MODULE NAVIGATION
    if (!isVendor) {
      list.push(
        {
          id: 'nav-dashboard',
          title: 'Operations Hub (Dashboard)',
          category: 'navigation',
          categoryLabel: 'Navigation (التنقل)',
          description: 'Main ERP executive dashboard and statistics',
          icon: FiLayers,
          path: '/',
          keywords: ['dashboard', 'home', 'hub', 'لوحة التحكم', 'الرئيسية'],
        },
        {
          id: 'nav-quotations',
          title: 'Quotations & Proposals',
          category: 'navigation',
          categoryLabel: 'Navigation (التنقل)',
          description: 'View all quotation drafts, revisions and approved quotes',
          icon: FiFileText,
          path: '/quotations',
          keywords: ['quotes', 'quotations', 'عروض الأسعار'],
        },
        {
          id: 'nav-invoices',
          title: 'Invoices & Billing',
          category: 'navigation',
          categoryLabel: 'Navigation (التنقل)',
          description: 'Tax invoices, credit notes and ZATCA integration',
          icon: FiCreditCard,
          path: '/invoices',
          keywords: ['invoices', 'billing', 'الفواتير'],
        },
        {
          id: 'nav-procurement',
          title: 'Procurement & Purchase Orders',
          category: 'navigation',
          categoryLabel: 'Navigation (التنقل)',
          description: 'Purchase requisitions, supplier POs and receipts',
          icon: FiShoppingCart,
          path: '/procurement',
          keywords: ['procurement', 'purchase orders', 'المشتريات'],
        },
        {
          id: 'nav-email-hub',
          title: 'ProcureFlow Mail & Corporate Webmail',
          category: 'navigation',
          categoryLabel: 'Navigation (التنقل)',
          description: 'Access company inboxes, RFQs, local archive (port 5000) & live webmail',
          icon: FiMail,
          path: '/procurement/email',
          keywords: ['email', 'mail', 'webmail', 'inbox', 'procureflow', 'cpanel', 'outlook', 'بريد', 'إيميل'],
        },
        {
          id: 'nav-inventory',
          title: 'Inventory & Stock Catalog',
          category: 'navigation',
          categoryLabel: 'Navigation (التنقل)',
          description: 'Manage warehouse stock, items and categories',
          icon: FiPackage,
          path: '/inventory',
          keywords: ['inventory', 'stock', 'warehouse', 'المخزون'],
        },
        {
          id: 'nav-customers',
          title: 'Customers Directory',
          category: 'navigation',
          categoryLabel: 'Navigation (التنقل)',
          description: 'Corporate client list, statements and contacts',
          icon: FiUsers,
          path: '/customers',
          keywords: ['customers', 'clients', 'العملاء'],
        },
        {
          id: 'nav-hr',
          title: 'HR & Workforce Directory',
          category: 'navigation',
          categoryLabel: 'Navigation (التنقل)',
          description: 'Employee directory, leaves, WPS payroll and contracts',
          icon: FiUsers,
          path: '/hr',
          keywords: ['hr', 'employees', 'workforce', 'الموارد البشرية'],
        },
        {
          id: 'nav-accounting',
          title: 'Accounting & Ledger',
          category: 'navigation',
          categoryLabel: 'Navigation (التنقل)',
          description: 'Chart of accounts, general ledger and journal entries',
          icon: SaudiRiyalIcon,
          path: '/accounting',
          keywords: ['accounting', 'ledger', 'المحاسبة', 'دفتر الأستاذ'],
        },
        {
          id: 'nav-settings',
          title: 'System Settings',
          category: 'navigation',
          categoryLabel: 'Navigation (التنقل)',
          description: 'Company registration, VAT settings and preferences',
          icon: FiSettings,
          path: '/settings',
          keywords: ['settings', 'config', 'الإعدادات'],
        }
      );
    }

    if (isSuperAdmin) {
      list.push({
        id: 'nav-global-admin',
        title: 'Global Admin Portal',
        category: 'navigation',
        categoryLabel: 'Navigation (التنقل)',
        description: 'Multi-tenant administration and organization setup',
        icon: FiBriefcase,
        path: '/admin/global',
        badge: 'SuperAdmin',
        badgeColor: 'red',
        keywords: ['global admin', 'superadmin', 'multi tenant', 'المدير العام'],
      });
    }

    return list;
  }, [isVendor, isAdmin, isSuperAdmin, role]);

  // Query live records using active deployed Convex queries
  const isConvexOrg = selectedOrganization && isConvexOrgId(selectedOrganization);
  const customersList = useQuery(
    api.customers.listCustomers,
    isOpen && isConvexOrg && !isVendor
      ? { organization_id: selectedOrganization as any }
      : 'skip'
  );

  const inventoryList = useQuery(
    api.inventory.listInventory,
    isOpen && isConvexOrg && !isVendor
      ? { organization_id: selectedOrganization as any }
      : 'skip'
  );

  const invoicesList = useQuery(
    api.invoiceQueries.getInvoices,
    isOpen && isConvexOrg && !isVendor
      ? { organization_id: selectedOrganization as any, limit: 40 }
      : 'skip'
  );

  // Filter static actions based on search query
  const filteredActions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      // Default: show quick actions first, then navigation
      return availableActions;
    }
    return availableActions.filter((item) => {
      const matchTitle = item.title.toLowerCase().includes(q);
      const matchTitleAr = item.titleAr ? item.titleAr.includes(q) : false;
      const matchDesc = item.description ? item.description.toLowerCase().includes(q) : false;
      const matchKeywords = item.keywords.some((k) => k.toLowerCase().includes(q));
      return matchTitle || matchTitleAr || matchDesc || matchKeywords;
    });
  }, [availableActions, searchQuery]);

  // Combined Results (Quick Actions -> Live Records -> Navigation)
  const combinedResults = useMemo(() => {
    const list: Array<CommandPaletteAction> = [];
    const q = searchQuery.trim().toLowerCase();

    // 1. Add matching quick actions
    const quickActions = filteredActions.filter((a) => a.category === 'quick_action');
    list.push(...quickActions);

    // 2. Add live database record matches (when user enters search query)
    if (q.length >= 2) {
      // A. Matching Customers
      if (customersList && Array.isArray(customersList)) {
        let count = 0;
        for (const c of customersList) {
          const nameEn = (c.name || '').toLowerCase();
          const nameAr = (c.name_arabic || '').toLowerCase();
          const cr = (c.cr_number || '').toLowerCase();
          const phone = (c.phone || '').toLowerCase();

          if (nameEn.includes(q) || nameAr.includes(q) || cr.includes(q) || phone.includes(q)) {
            list.push({
              id: `cust-${c._id}`,
              title: c.name || c.name_arabic || 'Customer',
              category: 'record',
              categoryLabel: 'Customers & Accounts (العملاء)',
              description: `CR: ${c.cr_number || 'N/A'} • Phone: ${c.phone || 'N/A'}`,
              icon: FiUsers,
              path: '/customers',
              badge: 'Client',
              badgeColor: 'blue',
              keywords: [c.name, c.cr_number || ''],
            });
            count++;
            if (count >= 3) break;
          }
        }
      }

      // B. Matching Invoices
      if (invoicesList && Array.isArray(invoicesList)) {
        let count = 0;
        for (const inv of invoicesList) {
          const invNum = (inv.invoice_number || '').toLowerCase();
          const invCust = (inv.customer_name || '').toLowerCase();

          if (invNum.includes(q) || invCust.includes(q)) {
            list.push({
              id: `inv-${inv._id}`,
              title: `${inv.invoice_number} - ${inv.customer_name || 'Invoice'}`,
              category: 'record',
              categoryLabel: 'Invoices & Billing (الفواتير)',
              description: `Amount: SAR ${inv.total_amount?.toLocaleString() || '0'} • Status: ${inv.status || 'Issued'}`,
              icon: FiCreditCard,
              path: '/invoices',
              badge: inv.invoice_type || 'Tax Invoice',
              badgeColor: 'green',
              keywords: [inv.invoice_number, inv.customer_name || ''],
            });
            count++;
            if (count >= 3) break;
          }
        }
      }

      // C. Matching Inventory Items
      if (inventoryList && Array.isArray(inventoryList)) {
        let count = 0;
        for (const item of inventoryList) {
          const nameEn = (item.name || '').toLowerCase();
          const nameAr = (item.name_arabic || '').toLowerCase();
          const sku = (item.sku || '').toLowerCase();

          if (nameEn.includes(q) || nameAr.includes(q) || sku.includes(q)) {
            list.push({
              id: `item-${item._id}`,
              title: item.name || item.name_arabic || 'Item',
              category: 'record',
              categoryLabel: 'Inventory & Stock (المخزون)',
              description: `SKU: ${item.sku} • Stock: ${item.quantity || 0} ${item.unit || 'units'} • SAR ${item.selling_price || 0}`,
              icon: FiPackage,
              path: '/inventory',
              badge: 'Stock',
              badgeColor: 'orange',
              keywords: [item.name, item.sku],
            });
            count++;
            if (count >= 3) break;
          }
        }
      }
    }

    // 3. Add navigation items
    const navActions = filteredActions.filter((a) => a.category === 'navigation');
    list.push(...navActions);

    return list;
  }, [filteredActions, customersList, inventoryList, invoicesList, searchQuery]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [combinedResults.length, searchQuery]);

  // Auto-focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Execute selected item
  const handleSelect = (item: CommandPaletteAction) => {
    onClose();
    if (item.onSelect) {
      item.onSelect();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  // Keyboard navigation within palette
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (combinedResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % combinedResults.length);
      scrollIntoView((selectedIndex + 1) % combinedResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + combinedResults.length) % combinedResults.length);
      scrollIntoView((selectedIndex - 1 + combinedResults.length) % combinedResults.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const current = combinedResults[selectedIndex];
      if (current) {
        handleSelect(current);
      }
    }
  };

  const scrollIntoView = (index: number) => {
    const el = document.getElementById(`palette-item-${index}`);
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
      <ModalContent
        bg={modalBg}
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="2xl"
        border="1px solid"
        borderColor={borderColor}
        my="10vh"
        maxH="80vh"
      >
        {/* Search Header */}
        <Box p={3} borderBottom="1px solid" borderColor={borderColor}>
          <InputGroup size="lg">
            <InputLeftElement pointerEvents="none">
              <Icon as={FiSearch} color="blue.500" boxSize={5} />
            </InputLeftElement>
            <Input
              ref={inputRef}
              placeholder='Search actions, records, or type "make quotation", "add inventory"...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              variant="unstyled"
              fontSize="md"
              px={2}
              autoComplete="off"
            />
            {searchQuery && (
              <InputRightElement>
                {searchQuery.length >= 2 && !customersList && !inventoryList && !invoicesList ? (
                  <Spinner size="sm" color="blue.500" />
                ) : (
                  <HStack spacing={1} pr={2}>
                    <Kbd fontSize="2xs">ESC to exit</Kbd>
                  </HStack>
                )}
              </InputRightElement>
            )}
          </InputGroup>
        </Box>

        {/* Results List */}
        <Box
          ref={listRef}
          maxH="55vh"
          overflowY="auto"
          p={2}
          css={{
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '4px' },
          }}
        >
          {combinedResults.length === 0 ? (
            <Box py={10} textAlign="center">
              <Text color={textMuted} fontSize="sm">
                No matching actions or records found for "{searchQuery}".
              </Text>
              <Text color={textMuted} fontSize="xs" mt={1}>
                Try typing <Text as="span" fontWeight="bold">"make"</Text>, <Text as="span" fontWeight="bold">"leave"</Text>, or an account name.
              </Text>
            </Box>
          ) : (
            <VStack spacing={1} align="stretch">
              {combinedResults.map((item, idx) => {
                const isSelected = idx === selectedIndex;
                const ItemIcon = item.icon;

                // Group header if category changes
                const prevItem = combinedResults[idx - 1];
                const showCategoryHeader = !prevItem || prevItem.categoryLabel !== item.categoryLabel;

                return (
                  <React.Fragment key={item.id}>
                    {showCategoryHeader && (
                      <Box px={3} pt={idx === 0 ? 1 : 3} pb={1}>
                        <Text
                          fontSize="2xs"
                          fontWeight="bold"
                          textTransform="uppercase"
                          color={textMuted}
                          letterSpacing="wider"
                        >
                          {item.categoryLabel}
                        </Text>
                      </Box>
                    )}
                    <Box
                      id={`palette-item-${idx}`}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      cursor="pointer"
                      p={3}
                      borderRadius="xl"
                      bg={isSelected ? activeItemBg : 'transparent'}
                      border="1px solid"
                      borderColor={isSelected ? highlightBorder : 'transparent'}
                      transition="all 0.15s ease"
                    >
                      <HStack justify="space-between" spacing={3}>
                        <HStack spacing={3} flex={1} overflow="hidden">
                          <Box
                            p={2}
                            borderRadius="lg"
                            bg={isSelected ? 'blue.500' : inputBg}
                            color={isSelected ? 'white' : 'blue.500'}
                            transition="all 0.15s ease"
                          >
                            <Icon as={ItemIcon} boxSize={4} />
                          </Box>
                          <VStack align="flex-start" spacing={0} overflow="hidden">
                            <HStack spacing={2}>
                              <Text
                                fontSize="sm"
                                fontWeight={isSelected ? 'bold' : 'medium'}
                                color={textPrimary}
                                isTruncated
                              >
                                {item.title}
                              </Text>
                              {item.titleAr && (
                                <Text fontSize="xs" color={textMuted} isTruncated>
                                  ({item.titleAr})
                                </Text>
                              )}
                              {item.badge && (
                                <Badge
                                  size="sm"
                                  colorScheme={item.badgeColor || 'gray'}
                                  borderRadius="full"
                                  px={2}
                                  fontSize="2xs"
                                >
                                  {item.badge}
                                </Badge>
                              )}
                            </HStack>
                            {item.description && (
                              <Text fontSize="xs" color={textMuted} isTruncated maxW="520px">
                                {item.description}
                              </Text>
                            )}
                          </VStack>
                        </HStack>

                        {isSelected && (
                          <HStack spacing={1} color="blue.500">
                            <Text fontSize="2xs" fontWeight="bold">Jump</Text>
                            <Icon as={FiCornerDownLeft} boxSize={3.5} />
                          </HStack>
                        )}
                      </HStack>
                    </Box>
                  </React.Fragment>
                );
              })}
            </VStack>
          )}
        </Box>

        {/* Footer Shortcut Bar */}
        <Box px={4} py={2.5} bg={inputBg} borderTop="1px solid" borderColor={borderColor}>
          <HStack justify="space-between" fontSize="2xs" color={textMuted}>
            <HStack spacing={4}>
              <HStack spacing={1}>
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd>
                <Text>Navigate</Text>
              </HStack>
              <HStack spacing={1}>
                <Kbd>↵</Kbd>
                <Text>Select / Execute</Text>
              </HStack>
              <HStack spacing={1}>
                <Kbd>ESC</Kbd>
                <Text>Close</Text>
              </HStack>
            </HStack>
            <HStack spacing={1}>
              <Text>Role:</Text>
              <Badge colorScheme={isAdmin ? 'purple' : 'teal'} fontSize="2xs">
                {role.toUpperCase()}
              </Badge>
            </HStack>
          </HStack>
        </Box>
      </ModalContent>
    </Modal>
  );
};
